import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, Undo2, Sparkles, RefreshCw } from "lucide-react";
import { CtaButton, MonoLabel, Tag, SecondaryButton } from "../components/kit";
import api, { formatApiErrorDetail } from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LandingEditorV2({ id }) {
  const navigate = useNavigate();
  const [landing, setLanding] = useState(undefined);
  const [msg, setMsg] = useState("");
  const [remember, setRemember] = useState(false);
  const [savedHint, setSavedHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pixel, setPixel] = useState("");
  const [iframeKey, setIframeKey] = useState(0);
  const pollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/landing/${id}`);
    setLanding(data);
    setPixel(data.pixel_id || "");
    return data;
  };
  useEffect(() => {
    load().catch(() => setLanding(null));
    api.get("/landing/jobs/status").then(({ data }) => {
      if (data.status === "pending" && data.kind === "refine") { setBusy(true); poll(); }
    }).catch(() => {});
    return () => pollRef.current && clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const poll = () => {
    pollRef.current && clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/landing/jobs/status");
        if (data.status === "complete") {
          clearInterval(pollRef.current); setBusy(false);
          await load(); setIframeKey((k) => k + 1);
          toast("Revision applied.", { description: "Not happy? Hit Undo." });
        } else if (data.status === "failed") {
          clearInterval(pollRef.current); setBusy(false);
          toast.error(data.error || "The revision failed.");
        }
      } catch { /* keep */ }
    }, 4000);
  };

  const refine = async () => {
    const text = msg.trim();
    if (text.length < 3) return;
    setBusy(true);
    setMsg("");
    setLanding((l) => ({ ...l, refine_messages: [...(l.refine_messages || []), { role: "user", text }] }));
    try {
      await api.post(`/landing/${id}/refine`, { message: text, remember_instruction: !!remember });
      if (remember) {
        setSavedHint(true);
        setTimeout(() => setSavedHint(false), 3500);
        setRemember(false);
      }
      poll();
    } catch (err) {
      setBusy(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const undo = async () => {
    try {
      await api.post(`/landing/${id}/undo`);
      await load(); setIframeKey((k) => k + 1);
      toast("Reverted to the previous version.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const savePixel = async () => {
    try {
      const { data } = await api.patch(`/landing/${id}`, { pixel_id: pixel });
      setLanding(data); setIframeKey((k) => k + 1);
      toast(pixel ? "Pixel saved." : "Pixel removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const togglePublish = async () => {
    try {
      if (landing.status === "published") { await api.post(`/landing/${id}/unpublish`); setLanding({ ...landing, status: "draft" }); }
      else { await api.post(`/landing/${id}/publish`); setLanding({ ...landing, status: "published" }); toast("Published.", { description: `/l/${landing.slug} is live.` }); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (landing === undefined) return <div className="p-10"><div className="card-soft h-40 animate-pulse" /></div>;
  if (landing === null) return <div className="p-10">Landing not found.</div>;

  const refinesLeft = 15 - (landing.refine_count || 0);

  return (
    <div className="p-4 sm:p-6 h-full flex flex-col" data-testid="landing-editor-v2">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => navigate("/app/performance/landing")} className="flex items-center gap-1.5 text-[13px] font-bold text-[#6E6D68] hover:text-[#1D1E26]" data-testid="v2-back-btn">
          <ArrowLeft size={14} /> Landings
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <Tag color={landing.status === "published" ? "green" : "neutral"}>{landing.status.toUpperCase()}</Tag>
          <Tag color="lilac">BESPOKE</Tag>
          {landing.status === "published" && (
            <a href={`/l/${landing.slug}`} target="_blank" rel="noreferrer" className="text-[12.5px] font-bold underline text-[#6E6D68] hover:text-[#1D1E26]" data-testid="v2-view-live">/l/{landing.slug}</a>
          )}
          <input className="field-input !py-1.5 !text-[12px] w-[140px]" placeholder="Meta Pixel ID" value={pixel} onChange={(e) => setPixel(e.target.value)} data-testid="v2-pixel-input" />
          <SecondaryButton className="!py-1.5 !px-3 !text-[11.5px]" onClick={savePixel} data-testid="v2-pixel-save">Save pixel</SecondaryButton>
          <CtaButton className="!py-2 !px-4 text-[13px]" onClick={togglePublish} data-testid="v2-publish-btn">
            {landing.status === "published" ? "Unpublish" : "Publish"}
          </CtaButton>
        </div>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-4 mt-4 flex-1 min-h-0">
        <div className="card-soft p-4 flex flex-col max-h-[75vh]" data-testid="v2-chat-panel">
          <div className="flex items-center justify-between">
            <MonoLabel>REFINE WITH AI</MonoLabel>
            <span className={`tag ${refinesLeft > 5 ? "tag-green" : refinesLeft > 0 ? "tag-amber" : "tag-rose"}`} data-testid="v2-refine-counter">{refinesLeft}/15 LEFT</span>
          </div>
          <div className="flex-1 overflow-y-auto mt-3 space-y-2 min-h-[120px]" data-testid="v2-chat-messages">
            {!(landing.refine_messages || []).length && (
              <p className="text-[12.5px] text-[#6E6D68] leading-relaxed">
                Tell the designer what to change, in plain words:<br />
                <em>"make the hero darker" · "add urgency to the offer" · "swap the benefits order" · "more premium tone"</em>
              </p>
            )}
            {(landing.refine_messages || []).map((m, i) => (
              <div key={i} className="rounded-xl bg-[#FAF8F5] border border-[#F1EFEA] px-3 py-2 text-[12.5px] text-[#1D1E26]" data-testid={`v2-msg-${i}`}>{m.text}</div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-[12.5px] text-[#96691E]" data-testid="v2-refining-state">
                <RefreshCw size={12} className="animate-spin" /> Reworking the page… (30-90s)
              </div>
            )}
          </div>
          <div className="mt-3">
            <textarea
              className="field-input !text-[13px] min-h-[64px]"
              placeholder="What should change?"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (!busy && refinesLeft > 0) refine(); } }}
              data-testid="v2-refine-input"
            />
            <div className="flex items-center gap-2 mt-2">
              <CtaButton className="!py-2 !px-4 text-[12.5px] flex-1" onClick={refine} disabled={busy || msg.trim().length < 3 || refinesLeft <= 0} data-testid="v2-refine-send">
                <Send size={12} /> {busy ? "Working…" : "Send"}
              </CtaButton>
              <SecondaryButton className="!py-2 !px-3 !text-[12.5px]" onClick={undo} disabled={busy || !(landing.versions_count > 0)} title={`${landing.versions_count || 0} versions back available`} data-testid="v2-undo-btn">
                <Undo2 size={13} /> Undo
              </SecondaryButton>
            </div>
            <label className="inline-flex items-center gap-2 text-[11.5px] text-[#6E6D68] mt-2 select-none cursor-pointer" data-testid="v2-remember-label">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                className="w-3.5 h-3.5 accent-[#181621]" data-testid="v2-remember-toggle" />
              Remember this instruction for future outputs
            </label>
            {savedHint && (
              <p className="text-[11px] text-[#1D1E26] font-semibold mt-1" data-testid="v2-remember-saved">
                Saved to your brand memory.
              </p>
            )}
            {landing.versions_count > 0 && <p className="text-[10.5px] text-[#A3A19B] mt-1.5">{landing.versions_count} previous version{landing.versions_count > 1 ? "s" : ""} kept</p>}
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden border border-[#E4E1DA] shadow-[0_4px_32px_rgba(29,30,38,0.08)] bg-white min-h-[75vh] relative" data-testid="v2-preview">
          {busy && (
            <div className="absolute inset-0 z-10 bg-white/70 backdrop-blur-[2px] flex items-center justify-center">
              <span className="flex items-center gap-2 text-[13px] font-bold text-[#1D1E26]"><Sparkles size={14} className="animate-pulse" /> Redesigning…</span>
            </div>
          )}
          <iframe key={iframeKey} title="preview" src={`${API}/api/landing/${id}/html`} className="w-full h-full min-h-[75vh] border-0" data-testid="v2-preview-iframe" />
        </div>
      </div>
    </div>
  );
}
